import React, { useEffect, useState } from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions, FeatureName } from '../../hooks/usePermissions';
import { AccessDenied } from './AccessDenied';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredFeature?: FeatureName;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredFeature }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const location = useLocation();
  const { canAccess, isLoading: permissionsLoading } = usePermissions();

  useEffect(() => {
    // console.log('ProtectedRoute: Checking auth status for path:', location.pathname);
    checkAuthStatus();
  }, [location.pathname]);

  const checkAuthStatus = async () => {
    try {
      await getCurrentUser();
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  // Still checking authentication status or permissions
  if (isAuthenticated === null || (requiredFeature && permissionsLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated but feature access denied
  if (requiredFeature && !canAccess(requiredFeature)) {
    return <AccessDenied featureName={requiredFeature} />;
  }

  // Authenticated and authorized, render children
  return <>{children}</>;
}; 